/**
 * masterChecklist.js — THE CPG AGREEMENT MASTER CHECKLIST.
 *
 * Eleven phases, in the order a brand meets them. Each row is tracked per project:
 *   status ("not-started" | "in-progress" | "signed" | "not-needed")
 *   counterparty, effectiveDate, expirationDate, attorneyReviewed, signedCopyStored, notes
 *
 * The `level` here is the checklist's own default. When a founder has completed the intake, the
 * plan's level overrides it — a document the intake marked required outranks a generic
 * "situational" default.
 */

export const CHECKLIST_PHASES = [
  {
    id: "before-you-start",
    title: "Before you start",
    order: 1,
    stage: "idea",
    blurb: "Ownership and confidentiality, while both are still cheap.",
    docs: [
      ["founder-agreement", "required"],
      ["co-founder-agreement", "required"],
      ["llc-operating-agreement", "required"],
      ["corporate-bylaws", "situational"],
      ["founder-ip-assignment", "required"],
      ["founder-invention-assignment", "required"],
      ["founder-vesting-agreement", "required"],
      ["piia", "recommended"],
      ["mutual-nda", "required"],
      ["one-way-nda", "recommended"],
      ["cap-table-documentation", "recommended"],
    ],
  },
  {
    id: "before-product-development",
    title: "Before product development",
    order: 2,
    stage: "development",
    blurb: "Anyone touching the formula signs before they start.",
    docs: [
      ["product-development-nda", "required"],
      ["product-development-agreement", "required"],
      ["formula-development-agreement", "required"],
      ["formula-ownership-agreement", "required"],
      ["formula-assignment-agreement", "required"],
      ["flavor-house-agreement", "recommended"],
      ["food-scientist-agreement", "recommended"],
      ["product-development-lab-agreement", "situational"],
      ["trade-secret-protection-agreement", "recommended"],
      ["recipe-formula-confidentiality-agreement", "recommended"],
    ],
  },
  {
    id: "before-manufacturing",
    title: "Before manufacturing",
    order: 3,
    stage: "pre-launch",
    blurb: "Everything the co-packer needs signed before a single case runs.",
    docs: [
      ["manufacturing-nda", "required"],
      ["co-packer-nda", "recommended"],
      ["co-packer-agreement", "required"],
      ["contract-manufacturing-agreement", "situational"],
      ["quality-agreement", "required"],
      ["product-specification-sheet", "required"],
      ["packaging-specification-agreement", "recommended"],
      ["recall-responsibility-agreement", "required"],
      ["food-safety-agreement", "recommended"],
      ["manufacturing-change-control-agreement", "recommended"],
      ["stability-testing-agreement", "recommended"],
      ["shelf-life-testing-agreement", "recommended"],
    ],
  },
  {
    id: "before-production",
    title: "Before production",
    order: 4,
    stage: "manufacturing",
    blurb: "Inputs, packaging and the terms that set your landed cost.",
    docs: [
      ["ingredient-supply-agreement", "required"],
      ["raw-material-supply-agreement", "recommended"],
      ["master-supply-agreement", "recommended"],
      ["supplier-quality-agreement", "recommended"],
      ["purchase-order-terms", "required"],
      ["packaging-supply-agreement", "required"],
      ["can-supply-agreement", "situational"],
      ["bottle-supply-agreement", "situational"],
      ["label-supply-agreement", "situational"],
      ["carton-supply-agreement", "situational"],
      ["packaging-artwork-ownership", "required"],
      ["supplier-nda", "situational"],
    ],
  },
  {
    id: "before-distribution",
    title: "Before distribution",
    order: 5,
    stage: "distribution",
    blurb: "The highest-stakes signatures in the whole business.",
    docs: [
      ["beverage-distributor-agreement", "required"],
      ["food-distributor-agreement", "required"],
      ["dsd-distributor-agreement", "situational"],
      ["exclusive-distribution-agreement", "situational"],
      ["non-exclusive-distribution-agreement", "recommended"],
      ["territory-distribution-agreement", "recommended"],
      ["master-distributor-agreement", "situational"],
      ["cpg-broker-agreement", "recommended"],
      ["commission-sales-agreement", "recommended"],
      ["three-pl-agreement", "required"],
      ["warehouse-agreement", "recommended"],
      ["transportation-agreement", "recommended"],
    ],
  },
  {
    id: "before-retail",
    title: "Before retail",
    order: 6,
    stage: "distribution",
    blurb: "Model the true net price after deductions before you accept the purchase order.",
    docs: [
      ["retail-vendor-agreement", "required"],
      ["wholesale-supply-agreement", "required"],
      ["retailer-supply-agreement", "recommended"],
      ["promotional-allowance-agreement", "recommended"],
      ["slotting-placement-agreement", "situational"],
      ["retail-marketing-agreement", "situational"],
      ["consignment-agreement", "situational"],
      ["sale-or-return-agreement", "situational"],
      ["demo-agreement", "situational"],
      ["sampling-agreement", "situational"],
    ],
  },
  {
    id: "before-marketing",
    title: "Before marketing",
    order: 7,
    stage: "growth",
    blurb: "Own what you paid for, and buy usage rights wide enough to actually use.",
    docs: [
      ["marketing-agency-agreement", "required"],
      ["packaging-design-agreement", "required"],
      ["design-ownership-agreement", "required"],
      ["graphic-design-agreement", "recommended"],
      ["photography-agreement", "required"],
      ["videography-agreement", "recommended"],
      ["content-release", "required"],
      ["model-release", "recommended"],
      ["influencer-agreement", "required"],
      ["ugc-creator-agreement", "required"],
      ["affiliate-agreement", "recommended"],
      ["influencer-disclosure", "required"],
      ["affiliate-disclosure", "recommended"],
      ["website-terms-of-use", "required"],
      ["privacy-policy", "required"],
    ],
  },
  {
    id: "before-hiring",
    title: "Before hiring",
    order: 8,
    stage: "growth",
    blurb: "Every person who makes something for the brand assigns it in writing.",
    docs: [
      ["employment-agreement", "required"],
      ["offer-letter", "required"],
      ["employee-confidentiality-agreement", "required"],
      ["employee-ip-assignment-agreement", "required"],
      ["independent-contractor-agreement", "required"],
      ["contractor-nda", "required"],
      ["contractor-work-for-hire-agreement", "required"],
      ["consultant-agreement", "recommended"],
      ["advisor-agreement", "recommended"],
      ["advisory-board-agreement", "situational"],
      ["independent-sales-rep-agreement", "situational"],
      ["commission-agreement", "situational"],
    ],
  },
  {
    id: "before-raising-capital",
    title: "Before raising capital",
    order: 9,
    stage: "fundraising",
    attorneyLed: true,
    blurb: "Securities documents. Arrive at your attorney prepared; do not paper a round yourself.",
    docs: [
      ["cap-table-documentation", "required"],
      ["founder-stock-documents", "required"],
      ["term-sheet", "required"],
      ["safe", "required"],
      ["convertible-note", "required"],
      ["preferred-stock-purchase-agreement", "recommended"],
      ["subscription-agreement", "recommended"],
      ["investor-rights-agreement", "recommended"],
      ["voting-agreement", "recommended"],
      ["rofr-co-sale-agreement", "recommended"],
      ["investor-nda", "situational"],
    ],
  },
  {
    id: "before-international",
    title: "Before international expansion",
    order: 10,
    stage: "growth",
    attorneyLed: true,
    blurb: "Country-specific counsel is not optional. Register the mark before you ship.",
    docs: [
      ["international-distributor-agreement", "required"],
      ["international-sales-agent-agreement", "recommended"],
      ["importer-agreement", "required"],
      ["export-agent-agreement", "situational"],
      ["international-supply-agreement", "situational"],
      ["international-manufacturing-agreement", "situational"],
      ["international-trademark-license-agreement", "required"],
      ["international-sales-rep-agreement", "situational"],
    ],
  },
  {
    id: "before-selling",
    title: "Before selling the company",
    order: 11,
    stage: "exit",
    attorneyLed: true,
    blurb: "Diligence surfaces every agreement you never signed. Fix what you can before you start.",
    docs: [
      ["due-diligence-nda", "required"],
      ["letter-of-intent", "required"],
      ["no-shop-agreement", "required"],
      ["asset-purchase-agreement", "required"],
      ["ma-stock-purchase-agreement", "recommended"],
      ["membership-interest-purchase-agreement", "recommended"],
      ["brand-acquisition-agreement", "recommended"],
      ["ip-purchase-agreement", "required"],
      ["transition-services-agreement", "required"],
      ["earn-out-agreement", "situational"],
    ],
  },
];

export const CHECKLIST_PHASE_BY_ID = Object.fromEntries(CHECKLIST_PHASES.map((p) => [p.id, p]));

export const CHECKLIST_STATUSES = [
  { id: "not-started", label: "Not started", tone: "slate" },
  { id: "in-progress", label: "In progress", tone: "amber" },
  { id: "signed", label: "Signed", tone: "emerald" },
  { id: "not-needed", label: "Not needed", tone: "zinc" },
];
export const CHECKLIST_STATUS_IDS = CHECKLIST_STATUSES.map((s) => s.id);

/** The per-row record the backend stores. */
export const EMPTY_CHECKLIST_ROW = {
  status: "not-started",
  counterparty: "",
  effectiveDate: "",
  expirationDate: "",
  attorneyReviewed: false,
  signedCopyStored: false,
  notes: "",
};

/** Progress across a phase, given a { [docId]: row } map. */
export function phaseProgress(phase, rows = {}) {
  const relevant = phase.docs.filter(([id]) => rows[id]?.status !== "not-needed");
  const done = relevant.filter(([id]) => rows[id]?.status === "signed").length;
  return { done, total: relevant.length, percent: relevant.length ? Math.round((done / relevant.length) * 100) : 0 };
}

/** Overall progress across every phase. */
export function checklistProgress(rows = {}) {
  let done = 0;
  let total = 0;
  for (const phase of CHECKLIST_PHASES) {
    const p = phaseProgress(phase, rows);
    done += p.done;
    total += p.total;
  }
  return { done, total, percent: total ? Math.round((done / total) * 100) : 0 };
}

/** Rows with an expiration or renewal date inside the next `days` days. */
export function upcomingRenewals(rows = {}, days = 90, today = new Date()) {
  const out = [];
  const horizon = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
  for (const [docId, row] of Object.entries(rows)) {
    if (!row?.expirationDate) continue;
    const d = new Date(row.expirationDate);
    if (Number.isNaN(d.getTime())) continue;
    if (d >= today && d <= horizon) out.push({ docId, ...row, daysAway: Math.ceil((d - today) / 86400000) });
  }
  return out.sort((a, b) => a.daysAway - b.daysAway);
}
